import type { ReactNode } from 'react'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { Button, Result, Space, Spin } from 'antd'
import { House, RotateCcw } from 'lucide-react'

type RouterFeedbackLayoutProps = {
  actions?: ReactNode
  children: ReactNode
  hint: string
  icon?: ReactNode
  status: '404' | 'info' | 'warning'
  title: string
  titleId: string
}

function RouterFeedbackLayout({ actions, children, hint, icon, status, title, titleId }: RouterFeedbackLayoutProps) {
  return (
    <main className="bg-page flex min-h-dvh items-center justify-center p-5 sm:p-8">
      <section
        aria-labelledby={titleId}
        className="border-border bg-surface w-full max-w-xl overflow-hidden rounded-2xl border shadow-[0_0.75rem_2.5rem_rgb(0_0_0_/_6%)]"
      >
        <Result
          className="px-5 py-10 sm:px-10 sm:py-12"
          extra={
            actions ? (
              <Space size={12} wrap>
                {actions}
              </Space>
            ) : undefined
          }
          icon={icon}
          status={status}
          subTitle={<span className="text-content-muted text-sm leading-6">{children}</span>}
          title={
            <h1 className="text-content m-0 text-2xl font-semibold tracking-[-0.025em] sm:text-[1.75rem]" id={titleId}>
              {title}
            </h1>
          }
        />
        <div className="border-border bg-surface-muted text-content-soft border-t px-6 py-4 text-center text-xs leading-5 sm:px-10">
          {hint}
        </div>
      </section>
    </main>
  )
}

export function RouterPending() {
  return (
    <div aria-live="polite" role="status">
      <RouterFeedbackLayout
        hint="通常只需几秒，请不要重复提交或刷新页面。"
        icon={<Spin aria-label="页面加载中" size="large" />}
        status="info"
        title="正在准备页面"
        titleId="router-pending-title"
      >
        系统正在同步当前页面需要的信息，请稍候。
      </RouterFeedbackLayout>
    </div>
  )
}

export function RouterError({ reset }: ErrorComponentProps) {
  const router = useRouter()

  const handleRetry = () => {
    reset()
    void router.invalidate()
  }

  return (
    <div role="alert">
      <RouterFeedbackLayout
        actions={
          <>
            <Button icon={<RotateCcw aria-hidden size={16} />} onClick={handleRetry} type="primary">
              重新加载
            </Button>
            <Button href="/" icon={<House aria-hidden size={16} />}>
              返回首页
            </Button>
          </>
        }
        hint="多次重试仍未恢复，请联系管理员处理。"
        status="warning"
        title="页面暂时无法加载"
        titleId="router-error-title"
      >
        服务暂时没有响应，当前地址已经保留。你可以重新加载，或者返回首页继续使用系统。
      </RouterFeedbackLayout>
    </div>
  )
}

export function RouterNotFound() {
  return (
    <RouterFeedbackLayout
      actions={
        <Button href="/" icon={<House aria-hidden size={16} />} type="primary">
          返回首页
        </Button>
      }
      hint="请检查当前地址，或从首页重新进入对应功能。"
      status="404"
      title="这里没有对应页面"
      titleId="router-not-found-title"
    >
      当前地址可能已经失效，或者页面已经被移动。
    </RouterFeedbackLayout>
  )
}
